import { ChevronDown } from "lucide-mui";
import Button, { type ButtonProps } from "@mui/material/Button";
import IconButton, { type IconButtonProps } from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Tooltip from "@mui/material/Tooltip";
import { useId, useState, type MouseEvent, type ReactNode } from "react";

interface CommonProps {
  /** MenuItems (plus ListSubheader / Divider). Clicking an item closes the
   *  menu after its own onClick; call `e.stopPropagation()` there to keep it open.
   *  A function gets `close`, for content that isn't a menu item (a date field's "Set"). */
  children: ReactNode | ((close: () => void) => ReactNode);
  /** Which edge of the trigger the menu lines up with. */
  align?: "left" | "right";
  /** The menu is never narrower than its trigger, nor than this. */
  minWidth?: number;
  tooltip?: ReactNode;
  onOpenChange?: (open: boolean) => void;
}

interface LabelTriggerProps extends CommonProps {
  /** A text button with a trailing caret. */
  label: ReactNode;
  icon?: never;
  "aria-label"?: string;
  buttonProps?: Omit<ButtonProps, "onClick" | "children" | "endIcon">;
}

interface IconTriggerProps extends CommonProps {
  /** An icon-only button (a kebab, an avatar); it needs an aria-label. */
  icon: ReactNode;
  label?: never;
  "aria-label": string;
  buttonProps?: Omit<IconButtonProps, "onClick" | "children">;
}

export type DropdownMenuProps = LabelTriggerProps | IconTriggerProps;

/**
 * A button that opens a menu of actions: the WAI-ARIA menu button. A labelled
 * trigger always carries one trailing caret, flipped while open; a leading
 * icon only when it says something the label doesn't.
 */
export default function DropdownMenu(props: DropdownMenuProps) {
  const { children, align = "left", minWidth, tooltip, onOpenChange } = props;
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  // Kept past close so the menu doesn't shrink while it fades out.
  const [triggerWidth, setTriggerWidth] = useState(0);
  const id = useId();
  const buttonId = `${id}-button`;
  const menuId = `${id}-menu`;
  const open = !!anchor;

  const setOpen = (el: HTMLElement | null) => {
    setAnchor(el);
    if (el) setTriggerWidth(el.offsetWidth);
    onOpenChange?.(!!el);
  };

  const triggerProps = {
    id: buttonId,
    "aria-label": props["aria-label"],
    "aria-haspopup": "menu" as const,
    "aria-controls": open ? menuId : undefined,
    "aria-expanded": open ? ("true" as const) : undefined,
    onClick: (e: MouseEvent<HTMLElement>) => setOpen(e.currentTarget),
  };

  let trigger: ReactNode;
  if (props.label !== undefined) {
    const { sx, ...rest } = props.buttonProps ?? {};
    trigger = (
      <Button
        {...rest}
        {...triggerProps}
        endIcon={<ChevronDown fontSize="small" />}
        sx={[
          {
            whiteSpace: "nowrap",
            flexShrink: 0,
            "& .MuiButton-endIcon": { ml: 0.5 },
            "& .MuiButton-endIcon svg": {
              transition: "transform 150ms",
              transform: open ? "rotate(180deg)" : "none",
            },
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {props.label}
      </Button>
    );
  } else {
    trigger = (
      <IconButton {...props.buttonProps} {...triggerProps}>
        {props.icon}
      </IconButton>
    );
  }

  return (
    <>
      {tooltip ? (
        // The span keeps the tooltip working on a disabled trigger.
        <Tooltip title={open ? "" : tooltip}>
          <span>{trigger}</span>
        </Tooltip>
      ) : (
        trigger
      )}
      <Menu
        id={menuId}
        anchorEl={anchor}
        open={open}
        onClose={() => setOpen(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: align }}
        transformOrigin={{ vertical: "top", horizontal: align }}
        slotProps={{
          list: {
            "aria-labelledby": buttonId,
            onClick: (e: MouseEvent<HTMLElement>) => {
              if ((e.target as HTMLElement).closest('[role="menuitem"]')) setOpen(null);
            },
          },
          paper: {
            sx: { mt: 0.5, minWidth: Math.max(minWidth ?? 0, triggerWidth) },
          },
        }}
      >
        {typeof children === "function" ? children(() => setOpen(null)) : children}
      </Menu>
    </>
  );
}
